/**
 * An on-screen keyboard, because the hardware has none.
 *
 * A kiosk panel is a touchscreen bolted to a wall. Windows' own touch keyboard
 * does not reliably appear for a webview running fullscreen with the shell in
 * kiosk mode, and when it does appear it is a desktop keyboard at desktop size
 * over a screen laid out for arm's length. So a search box with no keyboard of
 * its own is a search box that works on a developer's laptop and nowhere else.
 *
 * Cyrillic first and Latin behind a toggle, in that order: the menu is in
 * Mongolian, and the customer who needs Latin is the one who already switched
 * the interface to English and knows to look.
 *
 * This is deliberately not a general text-input component — it is three rows,
 * a space, a backspace and a clear. Anything that needs more than that on a
 * kiosk is a screen that should not exist.
 */
import { useState } from 'react'
import { useT } from '@/i18n'

/**
 * ФЦУЖЭН — the Mongolian layout, and not the Russian ЙЦУКЕН it is usually named
 * after. The three letter rows hold 34 keys and the alphabet has 35: on a
 * physical panel `е` sits on the digit row, and that is exactly how it went
 * missing here. A customer could not type `кофе`, `Американо`, `сендвич` or
 * `Элизабет` at all — prefix ranking hid it wherever `е` fell late enough in a
 * name for the letters before it to still match.
 */
const CYRILLIC = ['фцужэнгшүзкъ', 'йыбөахролдп', 'ячёсмитьвющ']

const LATIN = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm']

const DIGITS = '1234567890'

/** The digits, and the one letter the three letter rows cannot fit. Latin has no orphan. */
const digitRow = (script: 'cyrillic' | 'latin'): string => (script === 'cyrillic' ? `${DIGITS}е` : DIGITS)

export function Keyboard({
  onKey,
  onBackspace,
  onClear,
  onClose,
  code = false
}: {
  onKey: (character: string) => void
  onBackspace: () => void
  onClear: () => void
  onClose?: () => void
  /**
   * A code rather than words: Latin and digits only, with a shift key and no
   * space. A coupon is `Adjdkcmkj123`, and a keyboard that opened in Cyrillic
   * with no capitals could not type one.
   */
  code?: boolean
}) {
  const t = useT()
  const [script, setScript] = useState<'cyrillic' | 'latin'>(code ? 'latin' : 'cyrillic')
  const [shift, setShift] = useState(false)
  const rows = script === 'cyrillic' ? CYRILLIC : LATIN
  const cased = (character: string) => (shift ? character.toUpperCase() : character)

  return (
    <div className="flex flex-col gap-2.5">
      <Row>
        {[...digitRow(script)].map((character) => (
          <Key key={character} label={character} onPress={() => onKey(character)} />
        ))}
      </Row>

      {rows.map((row) => (
        <Row key={row}>
          {[...row].map((character) => (
            <Key key={character} label={cased(character)} onPress={() => onKey(cased(character))} />
          ))}
        </Row>
      ))}

      <Row>
        {code ? (
          <Key label={shift ? 'abc' : 'ABC'} wide onPress={() => setShift(!shift)} />
        ) : (
          <>
            <Key
              label={script === 'cyrillic' ? 'ABC' : 'АБВ'}
              wide
              onPress={() => setScript(script === 'cyrillic' ? 'latin' : 'cyrillic')}
            />
            <Key label="␣" wide onPress={() => onKey(' ')} />
          </>
        )}
        <Key label="⌫" onPress={onBackspace} />
        <Key label={t('menu.searchClear')} wide onPress={onClear} />
        {onClose !== undefined && <Key label={t('menu.keyboardClose')} wide onPress={onClose} />}
      </Row>
    </div>
  )
}

/**
 * A row of keys, sized by division rather than by guess.
 *
 * The longest Cyrillic row is twelve letters and the panel is 1080 wide, so a
 * fixed key width is a row that either wraps or leaves a gap depending on the
 * script. Every key is `flex-1` off a floor of 64px instead, which makes the
 * digits row and the ЙЦУКЕН row line up down their edges and keeps the wide
 * keys twice anything else whatever the layout is.
 */
const Row = ({ children }: { children: React.ReactNode }) => <div className="flex gap-2.5">{children}</div>

/**
 * One key.
 *
 * `onPointerDown` rather than `onClick`, and `preventDefault` with it: a tap
 * that goes through click costs a browser's 300ms disambiguation delay on some
 * panels, and the default would blur the field the characters are going into.
 */
function Key({ label, onPress, wide = false }: { label: string; onPress: () => void; wide?: boolean }) {
  return (
    <button
      type="button"
      onPointerDown={(event) => {
        event.preventDefault()
        onPress()
      }}
      className={`tap grid min-h-20 min-w-16 place-items-center rounded-[var(--radius-chip)] border border-[var(--color-line)] bg-white px-2 text-[length:var(--text-kiosk)] font-semibold shadow-sm active:bg-[var(--color-brand-soft)] ${
        wide ? 'flex-[2]' : 'flex-1'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * Ten digits and a backspace, for the two fields on this kiosk that take a
 * number: an e-Barimt registration number and an attendant's PIN.
 *
 * It is here rather than in each of those because a kiosk has no keyboard, and
 * a numeric `<input>` with nothing to type into it is a field that works on a
 * developer's laptop and nowhere else — which is exactly the state the company
 * receipt lookup shipped in.
 */
export function NumberPad({
  onKey,
  onBackspace,
  onClear
}: {
  onKey: (digit: string) => void
  onBackspace: () => void
  onClear?: () => void
}) {
  const t = useT()
  return (
    <div className="flex flex-col gap-3">
      {['123', '456', '789'].map((row) => (
        <Row key={row}>
          {[...row].map((digit) => (
            <Key key={digit} label={digit} onPress={() => onKey(digit)} />
          ))}
        </Row>
      ))}
      <Row>
        {onClear === undefined ? <span className="flex-1" /> : <Key label={t('menu.searchClear')} onPress={onClear} />}
        <Key label="0" onPress={() => onKey('0')} />
        <Key label="⌫" onPress={onBackspace} />
      </Row>
    </div>
  )
}
